import type { FileFolder } from "./types";

/** Название узла-заглушки: родителя нет в выдаче, известен только его идентификатор. */
export const UNAVAILABLE_FOLDER_NAME = "Недоступная папка";

export interface FolderNode {
  readonly id: string;
  readonly name: string;
  /** `null` у заглушки: сервер не отдал родительскую папку, и показать о ней нечего. */
  readonly folder: FileFolder | null;
  readonly children: FolderNode[];
}

const collator = new Intl.Collator("ru", {
  numeric: true,
  sensitivity: "base",
});

/** Сравнение названий для сортировки: по-русски, числа как числа («2» раньше «10»). */
export function compareNames(left: string, right: string): number {
  return collator.compare(left, right);
}

function byName(left: FileFolder, right: FileFolder): number {
  return (
    collator.compare(left.name, right.name) || left.id.localeCompare(right.id)
  );
}

/**
 * Собрать дерево из плоского списка сервера.
 *
 * Список плоский намеренно: во вложенном ответе одна повреждённая ветка
 * утащила бы за собой весь ответ. Поэтому защита от плохих данных живёт здесь:
 *
 * - родителя нет в выдаче (соседнему КПО открыта вложенная папка, а её предки
 *   принадлежат другой организации) — папка встаёт под узел-заглушку, а не
 *   пропадает и не роняет дерево;
 * - папки образуют цикл (сервер такого не допустит, но клиент не должен
 *   зависнуть в ожидании ответа) — цикл размыкается на первой по названию
 *   папке, и она становится корнем.
 */
export function buildFolderTree(folders: readonly FileFolder[]): FolderNode[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const childrenOf = new Map<string, FileFolder[]>();
  const roots: FileFolder[] = [];

  for (const folder of byId.values()) {
    if (folder.parent_id === null) {
      roots.push(folder);
      continue;
    }
    const siblings = childrenOf.get(folder.parent_id) ?? [];
    siblings.push(folder);
    childrenOf.set(folder.parent_id, siblings);
  }

  const visited = new Set<string>();
  const build = (folder: FileFolder): FolderNode => {
    visited.add(folder.id);
    const children = (childrenOf.get(folder.id) ?? [])
      .toSorted(byName)
      // Уже показанная папка во второй раз означает цикл: вглубь не идём.
      .filter((child) => !visited.has(child.id))
      .map(build);

    return { id: folder.id, name: folder.name, folder, children };
  };

  const tree = roots.toSorted(byName).map(build);

  const stubs: FolderNode[] = [];
  for (const [parentId, children] of childrenOf) {
    if (byId.has(parentId)) continue;
    const nodes = children
      .toSorted(byName)
      .filter((child) => !visited.has(child.id))
      .map(build);
    stubs.push({
      id: parentId,
      name: UNAVAILABLE_FOLDER_NAME,
      folder: null,
      children: nodes,
    });
  }

  // Всё, до чего не дошли от корней и заглушек, лежит в цикле.
  const looped = [...byId.values()]
    .filter((folder) => !visited.has(folder.id))
    .toSorted(byName);
  for (const folder of looped) {
    if (!visited.has(folder.id)) tree.push(build(folder));
  }

  return [
    ...tree.toSorted((a, b) => collator.compare(a.name, b.name)),
    ...stubs,
  ];
}

export function indexFolders(
  folders: readonly FileFolder[],
): ReadonlyMap<string, FileFolder> {
  return new Map(folders.map((folder) => [folder.id, folder]));
}

export interface FolderChainLink {
  /** `null` у заглушки на месте недоступной папки. */
  readonly id: string | null;
  readonly name: string;
}

/**
 * Цепочка папок от корня до указанной.
 *
 * Обход защищён от цикла и от недоступного родителя: на месте недоступного
 * звена стоит заглушка, и путь всё равно читается.
 */
export function folderChain(
  byId: ReadonlyMap<string, FileFolder>,
  id: string,
): FolderChainLink[] {
  const chain: FolderChainLink[] = [];
  const seen = new Set<string>();
  let cursor: string | null = id;

  while (cursor !== null) {
    const folder = byId.get(cursor);
    if (folder === undefined) {
      chain.unshift({ id: null, name: UNAVAILABLE_FOLDER_NAME });
      break;
    }
    if (seen.has(cursor)) break;
    seen.add(cursor);
    chain.unshift({ id: folder.id, name: folder.name });
    cursor = folder.parent_id;
  }

  return chain;
}

/** Названия папок от корня до указанной. */
export function folderPathNames(
  byId: ReadonlyMap<string, FileFolder>,
  id: string,
): string[] {
  return folderChain(byId, id).map((link) => link.name);
}

export function folderPathLabel(
  byId: ReadonlyMap<string, FileFolder>,
  id: string,
): string {
  return folderPathNames(byId, id).join(" / ");
}

/** Идентификаторы всех вложенных папок; сама папка не входит. Цикл не зацикливает обход. */
export function descendantFolderIds(
  folders: readonly FileFolder[],
  id: string,
): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const folder of folders) {
    if (folder.parent_id === null) continue;
    const siblings = childrenOf.get(folder.parent_id) ?? [];
    siblings.push(folder.id);
    childrenOf.set(folder.parent_id, siblings);
  }

  const found = new Set<string>();
  const queue = [id];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const child of childrenOf.get(current) ?? []) {
      if (child === id || found.has(child)) continue;
      found.add(child);
      queue.push(child);
    }
  }

  return found;
}
